// frontend-react/src/components/SchedulingPanel.js
import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Schedule, Edit } from "@mui/icons-material";
import { adminService, blogService } from "../api/services";
import { safeMap, safeArray } from "../utils/validation"; // Importar validación

const SchedulingPanel = () => {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchScheduledPosts();
  }, []);

  const fetchScheduledPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminService.getScheduledPosts();
      // Usar safeArray para garantizar que siempre sea un array
      setScheduledPosts(safeArray(response.data));
    } catch (error) {
      console.error("Error fetching scheduled posts:", error);
      setError("Error al cargar posts programados");
      setScheduledPosts([]); // Array vacío por defecto
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (post) => {
    setSelectedPost(post);
    setScheduleDate(post.scheduled_publish || "");
    setEditOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      await adminService.updatePostSchedule(selectedPost.id, {
        scheduled_publish: scheduleDate,
        status: scheduleDate ? "scheduled" : "draft",
      });
      setEditOpen(false);
      fetchScheduledPosts();
    } catch (error) {
      console.error("Error updating schedule:", error);
      setError("Error al actualizar programación");
    }
  };

  const getStatusChip = (post) => {
    const status = post.status;
    const color =
      status === "scheduled"
        ? "primary"
        : status === "published"
        ? "success"
        : "default";
    return <Chip label={status} color={color} size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Posts Programados
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Programado para</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Usar safeMap para garantizar que siempre funcione */}
                {safeMap(scheduledPosts, (post) => (
                  <TableRow key={post.id}>
                    <TableCell>{post.title}</TableCell>
                    <TableCell>{getStatusChip(post)}</TableCell>
                    <TableCell>
                      {post.scheduled_publish
                        ? new Date(post.scheduled_publish).toLocaleString()
                        : "No programado"}
                    </TableCell>
                    <TableCell>
                      <Button
                        startIcon={<Edit />}
                        onClick={() => handleEditSchedule(post)}
                        size="small"
                      >
                        Programar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {safeArray(scheduledPosts).length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No hay posts programados
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Programar Publicación</DialogTitle>
        <DialogContent>
          <TextField
            label="Fecha y Hora de Publicación"
            type="datetime-local"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveSchedule} variant="contained">
            Guardar Programación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchedulingPanel;
